// Settings · Industry pack (`/settings/industry-pack`, spec 02 §4 S13, D-343).
//
// The pack the factory was set up from, whether the deployed pack is newer, and
// the previewed update: what the new version adds (taken), changes (taken only
// when ticked), and drops (kept). It lives in settings, outside the wizard
// shell, because the tenant who needs it most is already live and the wizard is
// read-only history for them (spec 02 §3.1).
//
// API (generated api-client, doctrine 3):
//   GET  /v1/verticals/installs/current                  the installed row
//   GET  /v1/verticals/installs/current/update-preview   the per-object plan
//   POST /v1/verticals/installs/current/update           apply, 202
//
// Permission: `onboarding.install_pack` (group_admin). A reader without it sees
// the refusal sentence and the screen makes no request the server would refuse.

import { enumLabel } from "../../../lib/enum-label";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { Modal } from "../../../components/modal";
import { CardSkeleton, EmptyState, ErrorState, ForbiddenState } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { formatTimestamp } from "../../../lib/datetime";
import { UnauthorizedError } from "../../../lib/problem";
import { RequestError, requestIdOf } from "../../../lib/request";
import {
  useApplyPackUpdate,
  useCurrentInstall,
  usePackUpdatePreview,
  useVerticals,
  type PackUpdatePreview,
} from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import {
  acceptList,
  allTickable,
  appliesCount,
  applyProblemKey,
  canApply,
  entryId,
  groupByKind,
  isTickable,
  refusalRefreshesPreview,
  shortDigest,
} from "./pack-update/pack-update-model";
import "./pack-update/industry-pack.css";

export function IndustryPackScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useOnboardingPermissions();
  const allowed = perms.has("onboarding.install_pack");

  const current = useCurrentInstall({ pollWhileInstalling: true });
  const preview = usePackUpdatePreview(allowed);
  const verticals = useVerticals();
  const apply = useApplyPackUpdate();

  const [open, setOpen] = useState(false);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [refusal, setRefusal] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const unauthorized =
    current.error instanceof UnauthorizedError || preview.error instanceof UnauthorizedError;
  useEffect(() => {
    if (unauthorized) void navigate({ to: "/login" });
  }, [unauthorized, navigate]);

  const header = (
    <div>
      <PageHeader title={t("onboarding.packUpdate.title")} />
      <p className="ip-subtitle">{t("onboarding.packUpdate.subtitle")}</p>
    </div>
  );

  const install = current.data?.install;

  // The apply leaves the preview stale and unread, because read while the
  // update row is installing it answers 409 PACK_INSTALL_IN_PROGRESS. It is
  // read again once the polled install settles.
  const installStatus = install?.status;
  const refetchPreview = preview.refetch;
  const lastStatus = useRef(installStatus);
  useEffect(() => {
    const was = lastStatus.current;
    lastStatus.current = installStatus;
    if (
      allowed &&
      was === "installing" &&
      installStatus !== undefined &&
      installStatus !== "installing"
    ) {
      void refetchPreview();
    }
  }, [installStatus, allowed, refetchPreview]);

  const packName = useMemo(() => {
    const summary = verticals.data?.items.find((p) => p.key === install?.pack_key);
    if (summary === undefined) return install?.pack_key ?? "";
    return summary.name[i18n.language] ?? summary.name["en"] ?? install?.pack_key ?? "";
  }, [verticals.data, install?.pack_key, i18n.language]);

  if (!perms.isResolved || unauthorized) {
    return (
      <div className="ip-screen">
        {header}
        <CardSkeleton lines={4} label={t("onboarding.packUpdate.loading")} />
      </div>
    );
  }
  if (!allowed) {
    return (
      <div className="ip-screen">
        {header}
        <ForbiddenState
          title={t("onboarding.packUpdate.forbiddenTitle")}
          body={t("onboarding.packUpdate.forbiddenBody")}
        />
      </div>
    );
  }
  if (current.isPending) {
    return (
      <div className="ip-screen">
        {header}
        <CardSkeleton lines={4} label={t("onboarding.packUpdate.loading")} />
      </div>
    );
  }
  if (current.error !== null) {
    return (
      <div className="ip-screen">
        {header}
        <ErrorState
          title={t("onboarding.packUpdate.errorTitle")}
          detail={t("onboarding.packUpdate.errorDetail")}
          retryLabel={t("onboarding.state.retry")}
          onRetry={() => {
            void current.refetch();
          }}
          requestId={requestIdOf(current.error)}
          requestIdLabel={t("onboarding.state.requestId")}
        />
      </div>
    );
  }
  if (install === undefined) {
    return (
      <div className="ip-screen">
        {header}
        <EmptyState
          title={t("onboarding.packUpdate.emptyTitle")}
          body={t("onboarding.packUpdate.emptyBody")}
          ctaLabel={t("onboarding.packUpdate.emptyCta")}
          onCta={() => {
            void navigate({ to: "/onboarding/vertical" });
          }}
        />
      </div>
    );
  }

  const installing = install.status === "installing";
  const statusVariant = install.status === "failed" ? "bad" : installing ? "warn" : ("ok" as const);

  function openReview(): void {
    setTicked(new Set());
    setRefusal(null);
    setApplied(false);
    setOpen(true);
  }

  function submit(p: PackUpdatePreview): void {
    setRefusal(null);
    apply.mutate(
      { availableDigest: p.available_digest, accept: acceptList(p.changes, ticked) },
      {
        onSuccess: () => {
          setOpen(false);
          setApplied(true);
        },
        onError: (error) => {
          const code = error instanceof RequestError ? error.code : undefined;
          setRefusal(applyProblemKey(code));
          if (refusalRefreshesPreview(code)) void preview.refetch();
        },
      },
    );
  }

  return (
    <div className="ip-screen" data-testid="industry-pack">
      {header}

      <Card
        title={t("onboarding.packUpdate.installed.heading")}
        data-testid="industry-pack-installed"
      >
        <dl className="ip-facts">
          <dt>{t("onboarding.packUpdate.installed.pack")}</dt>
          <dd data-testid="industry-pack-name">{packName}</dd>
          <dt>{t("onboarding.packUpdate.installed.version")}</dt>
          <dd data-testid="industry-pack-version">{install.pack_version}</dd>
          <dt title={t("onboarding.packUpdate.installed.fingerprintHint")}>
            {t("onboarding.packUpdate.installed.fingerprint")}
          </dt>
          <dd>
            <code data-testid="industry-pack-digest">{shortDigest(install.pack_digest)}</code>
          </dd>
          <dt>{t("onboarding.packUpdate.installed.installedAt")}</dt>
          <dd data-testid="industry-pack-installed-at">
            {formatTimestamp(install.finished_at ?? install.created_at, i18n.language)}
          </dd>
          {preview.data ? (
            <>
              <dt>{t("onboarding.packUpdate.installed.live")}</dt>
              <dd data-testid="industry-pack-live" data-live={String(preview.data.live)}>
                {t(
                  preview.data.live
                    ? "onboarding.packUpdate.installed.liveYes"
                    : "onboarding.packUpdate.installed.liveNo",
                )}
              </dd>
            </>
          ) : null}
          <dt data-testid="industry-pack-mode" data-mode={install.mode}>
            {t(`onboarding.packUpdate.installed.mode.${install.mode}`)}
          </dt>
          <dd>
            <StatusChip variant={statusVariant} data-status={install.status}>
              {t(`onboarding.packUpdate.installed.status.${install.status}`)}
            </StatusChip>
          </dd>
        </dl>
        {installing ? (
          <p className="ip-note">{t("onboarding.packUpdate.installed.applying")}</p>
        ) : null}
        {install.status === "failed" ? (
          <p className="ip-note">{t("onboarding.packUpdate.installed.failed")}</p>
        ) : null}
        {applied && install.status === "installed" ? (
          <p className="ip-note" role="status" data-testid="industry-pack-applied">
            {t("onboarding.packUpdate.applied")}
          </p>
        ) : null}
      </Card>

      <Card
        title={t("onboarding.packUpdate.available.heading")}
        data-testid="industry-pack-available"
      >
        <AvailableBody
          preview={preview.data ?? null}
          isPending={preview.isPending}
          error={preview.error}
          installing={installing}
          onRetry={() => {
            void preview.refetch();
          }}
          onReview={openReview}
        />
      </Card>

      {open && preview.data ? (
        <Modal
          label={t("onboarding.packUpdate.drawer.label")}
          heading={t("onboarding.packUpdate.drawer.heading", {
            version: preview.data.available_version,
          })}
          closeLabel={t("onboarding.packUpdate.drawer.close")}
          onClose={() => {
            setOpen(false);
          }}
          pending={apply.isPending}
          testId="industry-pack-review"
        >
          <ReviewBody
            preview={preview.data}
            ticked={ticked}
            setTicked={setTicked}
            refusal={refusal}
            busy={apply.isPending}
            onCancel={() => {
              setOpen(false);
            }}
            onApply={() => {
              if (preview.data) submit(preview.data);
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}

function AvailableBody(props: {
  preview: PackUpdatePreview | null;
  isPending: boolean;
  error: unknown;
  installing: boolean;
  onRetry: () => void;
  onReview: () => void;
}) {
  const { t } = useTranslation();
  // An update in flight is the install card's to report. The preview cannot be
  // read until it settles (409 PACK_INSTALL_IN_PROGRESS), which is not a
  // failed read.
  const inFlight =
    props.installing ||
    (props.error instanceof RequestError && props.error.code === "PACK_INSTALL_IN_PROGRESS");
  if (inFlight) {
    return (
      <p className="ip-note" data-testid="industry-pack-available-applying">
        {t("onboarding.packUpdate.available.applying")}
      </p>
    );
  }
  if (props.isPending) {
    return <CardSkeleton lines={2} label={t("onboarding.packUpdate.loading")} />;
  }
  if (props.error !== null && props.error !== undefined) {
    return (
      <ErrorState
        title={t("onboarding.packUpdate.available.previewError")}
        retryLabel={t("onboarding.packUpdate.available.retry")}
        onRetry={props.onRetry}
        requestId={requestIdOf(props.error)}
        requestIdLabel={t("onboarding.state.requestId")}
      />
    );
  }
  const p = props.preview;
  if (p === null) return null;
  const declined = p.changes.filter(isTickable).length;
  const sentence = p.update_available
    ? t("onboarding.packUpdate.available.newer", { version: p.available_version })
    : declined > 0
      ? t("onboarding.packUpdate.available.upToDateWithDeclined", { count: declined })
      : t("onboarding.packUpdate.available.upToDate");
  return (
    <div className="ip-available">
      {p.update_available ? (
        <StatusChip variant="accent" data-testid="industry-pack-update-chip">
          {t("onboarding.packUpdate.available.chip")}
        </StatusChip>
      ) : null}
      <p data-testid="industry-pack-available-sentence">{sentence}</p>
      {p.update_available || p.can_apply ? (
        <p className="ip-note" data-testid="industry-pack-counts">
          {t("onboarding.packUpdate.available.counts", {
            added: p.counts.added,
            changed: p.counts.changed,
            removed: p.counts.removed,
            missing: p.counts.missing,
            acceptable: p.counts.acceptable,
          })}
        </p>
      ) : null}
      {p.can_apply ? (
        <Button variant="primary" onClick={props.onReview} data-testid="industry-pack-review-open">
          {t("onboarding.packUpdate.available.review")}
        </Button>
      ) : null}
    </div>
  );
}

function ReviewBody(props: {
  preview: PackUpdatePreview;
  ticked: Set<string>;
  setTicked: (next: Set<string>) => void;
  refusal: string | null;
  busy: boolean;
  onCancel: () => void;
  onApply: () => void;
}) {
  const { t } = useTranslation();
  const { preview, ticked, setTicked } = props;
  const groups = groupByKind(preview.changes);
  const tickable = allTickable(preview.changes);
  const accepted = acceptList(preview.changes, ticked).length;

  function toggle(id: string, on: boolean): void {
    const next = new Set(ticked);
    if (on) next.add(id);
    else next.delete(id);
    setTicked(next);
  }

  return (
    <div className="ip-dialog">
      <p className="ip-note">{t("onboarding.packUpdate.drawer.intro")}</p>
      {preview.live ? (
        <p className="ip-note">{t("onboarding.packUpdate.drawer.liveNote")}</p>
      ) : null}
      {groups.length === 0 ? <p>{t("onboarding.packUpdate.drawer.nothing")}</p> : null}
      {tickable.size > 0 ? (
        <div className="ip-actions">
          <Button
            size="sm"
            onClick={() => {
              setTicked(new Set(tickable));
            }}
          >
            {t("onboarding.packUpdate.drawer.selectAll")}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setTicked(new Set());
            }}
          >
            {t("onboarding.packUpdate.drawer.selectNone")}
          </Button>
        </div>
      ) : null}
      {groups.map((group) => (
        <section className="ip-group" key={group.kind} data-kind={group.kind}>
          <h4>{enumLabel(t, "onboarding.packUpdate.kinds", group.kind)}</h4>
          <ul className="ip-list">
            {group.entries.map((entry) => {
              const id = entryId(entry);
              return (
                <li
                  className="ip-row"
                  key={id}
                  data-entry={`${entry.object_kind}:${entry.key}`}
                  data-change={entry.change}
                  data-disposition={entry.disposition}
                >
                  {isTickable(entry) ? (
                    <label>
                      <input
                        type="checkbox"
                        checked={ticked.has(id)}
                        aria-label={`${t("onboarding.packUpdate.drawer.accept")}: ${entry.key}`}
                        onChange={(event) => {
                          toggle(id, event.target.checked);
                        }}
                      />
                      <code>{entry.key}</code>
                    </label>
                  ) : (
                    <code>{entry.key}</code>
                  )}
                  <StatusChip
                    variant={entry.change === "removed" ? "neutral" : "accent"}
                    dot={false}
                  >
                    {t(`onboarding.packUpdate.change.${entry.change}`)}
                  </StatusChip>
                  <span className="ip-note">
                    {t(`onboarding.packUpdate.disposition.${entry.disposition}`)}
                  </span>
                  {entry.fields.length > 0 ? (
                    <p className="ip-fields">
                      {t("onboarding.packUpdate.drawer.fields", {
                        fields: entry.fields.join(", "),
                      })}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      <p className="ip-note" data-testid="industry-pack-summary">
        {t("onboarding.packUpdate.drawer.summary.applies", {
          count: appliesCount(preview.changes),
        })}
        {" · "}
        {t("onboarding.packUpdate.drawer.summary.accepted", { count: accepted })}
      </p>
      {props.refusal !== null ? (
        <p className="ip-note" role="alert" data-testid="industry-pack-refusal">
          {t(`onboarding.packUpdate.problems.${props.refusal}`)}
        </p>
      ) : null}
      <div className="ip-actions">
        <Button onClick={props.onCancel} disabled={props.busy}>
          {t("onboarding.packUpdate.drawer.cancel")}
        </Button>
        <Button
          variant="primary"
          busy={props.busy}
          busyLabel={t("onboarding.packUpdate.drawer.applying")}
          disabled={!canApply(preview, ticked)}
          onClick={props.onApply}
          data-testid="industry-pack-apply"
        >
          {t("onboarding.packUpdate.drawer.apply")}
        </Button>
      </div>
    </div>
  );
}
